/**
 * Skill processor for the Now Assist Skill Plugin
 * Contains the main skill processing logic and helper functions
 */

import {
    NOW_LLM_GENERIC_PROVIDER_API,
    SYS_GEN_AI_SKILL_APPLICABILITY_CREATE_ONLY_DEFAULTS,
    VAR_SET,
    TABLE_NAMES,
    PROVIDER_MAPPINGS,
    SKILL_NAME_MAX_LENGTH,
    MAX_TOKENS_UPPER_BOUND,
    RESTRICTED_ROLE_NAMES,
    RESTRICTED_ROLE_SYS_IDS,
    SUPPORTED_FILTER_CONDITION_DATATYPES,
    STANDARD_OUTPUT_NAMES,
    STANDARD_OUTPUT_DEFAULTS,
    DEFAULT_CONDITION_SCRIPT,
} from './defaults'
import { applyDefaults, sanitizeSkillName } from './skill-plugin-helpers'
import type {
    Context,
    ObjectShape,
    CallExpressionShape,
    Record as BuildRecord,
    Shape,
} from '@servicenow/sdk-build-core'
import {
    TemplateExpressionShape,
    PropertyAccessShape,
    ElementAccessExpressionShape,
    isGUID,
} from '@servicenow/sdk-build-core'
import { NowIdShape, ArrowFunctionShape, NowIncludeShape } from '@servicenow/sdk-build-plugins'
import { processSecurityControls } from './security-processor'
import { processUIActionDeployment } from './deployment-processor'
import { processTools, normalizeInputFieldName, inputChainToTemplate } from './tool-processor'

/**
 * Extract prompt text from a version object shape.
 *
 * Handles two formats:
 * 1. String literal: returns the value as-is
 * 2. Arrow function: `(p) => \`...${p.tool.ToolName.output}...${p.input.fieldName}...\``
 *    Extracts the template literal body and converts expressions to mustache:
 *    - p.tool.ToolName.outputField  → {{ToolName.outputField}}
 *    - p.input.fieldName            → {{fieldname}} (normalized/lowercased)
 *
 * @returns The prompt string with mustache syntax, or empty string if not found
 */
function extractPromptValue(versionObj: ObjectShape): string {
    if (!versionObj.has('prompt')) {
        return ''
    }

    const promptShape = versionObj.get('prompt')

    // Case 1: String literal — use as-is
    if (promptShape?.isString()) {
        return promptShape.asString().getValue()
    }

    // Case 2: Arrow function — extract template literal and convert expressions
    if (promptShape instanceof ArrowFunctionShape) {
        const statements = promptShape.getStatements()
        const returnVal = promptShape.getReturnValue()
        // Support both block body (statements) and expression body (returnValue)
        const candidates = returnVal ? [...statements, returnVal] : statements
        for (const candidate of candidates) {
            if (candidate instanceof TemplateExpressionShape) {
                return convertTemplateToMustache(candidate)
            }
        }
        // Handle edge cases where the arrow function body is a plain string
        const singleExpr = statements[0] ?? returnVal
        if (singleExpr?.isString()) {
            return singleExpr.asString().getValue()
        }
    }

    // Fallback: try to get as string (may work for other shape types)
    try {
        return promptShape?.asString().getValue() ?? ''
    } catch {
        return ''
    }
}

/**
 * Convert a TemplateExpressionShape to a mustache-format prompt string.
 *
 * Processes each span's expression:
 * - PropertyAccessShape p.tool.ToolName.field           → {{ToolName.field}}
 * - PropertyAccessShape p.input.fieldName               → {{fieldname}} (normalized)
 * - PropertyAccessShape p.input.ACE.assigned_to.active  → {{ace.assigned_to.active}} (deep dot-walk)
 * - ElementAccessExpressionShape p.input['field name']  → {{field_name}} (bracket notation)
 * - ElementAccessExpressionShape p.input.ACE['field']   → {{ace.field}} (nested bracket)
 * - Fallback via getCode() for complex chains (e.g., p.input.jsonArray[0].name)
 */
function convertTemplateToMustache(template: TemplateExpressionShape): string {
    let result = template.getLiteralText()

    for (const span of template.getSpans()) {
        const expression = span.getExpression()
        let replacement = ''

        if (expression instanceof PropertyAccessShape) {
            const elements = expression.getElements()
            const elementNames = elements.map((e) => e.getName())

            // Pattern: p.tool.ToolName.outputField → {{ToolName.outputField}}
            if (elementNames.length >= 4 && elementNames[1] === 'tool') {
                const toolName = elementNames[2]
                const outputField = elementNames.slice(3).join('.')
                replacement = `{{${toolName}.${outputField}}}`
            }
            // Pattern: p.input.fieldName[.deepWalk...] → {{fieldname[.deepwalk...]}}
            else if (elementNames.length >= 3 && elementNames[1] === 'input') {
                const fieldName = elementNames[2]!
                const normalized = normalizeInputFieldName(fieldName)
                if (elementNames.length === 3) {
                    replacement = `{{${normalized}}}`
                } else {
                    // Deep dot-walk: p.input.ACE.assigned_to.active → {{ace.assigned_to.active}}
                    const rest = elementNames.slice(3).join('.')
                    replacement = `{{${normalized}.${rest}}}`
                }
            }
            // Unknown pattern — use code representation
            else {
                replacement = expression.getCode()
            }
        } else if (expression instanceof ElementAccessExpressionShape) {
            // Pattern: p.input['field name'] → {{field_name}} (normalized)
            // Handles bracket notation for input fields with spaces or special characters
            const callee = expression.getCallee() // e.g., "p.input"
            const arg = expression.getArg() // e.g., StringShape("field name")

            if (callee.endsWith('.input') && arg.isString()) {
                const fieldName = arg.asString().getValue()
                replacement = `{{${normalizeInputFieldName(fieldName)}}}`
            }
            // Pattern: p.input.ACE['assigned_to'] → {{ace.assigned_to}}
            // Pattern: p.input.jsonArray[0] → {{jsonarray.[0]}}
            else if (callee.includes('.input.')) {
                const match = callee.match(/\.input\.(.+)$/)
                if (match && match[1]) {
                    const chain = match[1]
                    if (arg.isString()) {
                        replacement = `{{${inputChainToTemplate(chain)}.${arg.asString().getValue()}}}`
                    } else {
                        // Non-string arg (e.g., numeric array index)
                        const argCode = arg.getCode()
                        replacement = `{{${inputChainToTemplate(chain)}.[${argCode}]}}`
                    }
                } else {
                    replacement = expression.getCode()
                }
            } else {
                // Unknown pattern — reconstruct bracket notation
                replacement = expression.getCode()
            }
        } else {
            // Fallback: detect input references from code representation
            // Handles complex chains like p.input.jsonArray[0].name
            const code = expression.getCode()
            const inputMatch = code?.match(/^[a-zA-Z_$]+\.input\.(.+)$/)
            if (inputMatch && inputMatch[1]) {
                replacement = `{{${inputChainToTemplate(inputMatch[1])}}}`
            } else {
                replacement = code || ''
            }
        }

        result += replacement + span.getLiteralText()
    }

    return result
}

/**
 * Validate that names contain only alphanumeric characters and spaces
 * @param str - Name to validate (skill name, attribute name, etc.)
 * @param diagnostics - Diagnostics object for error reporting
 * @param source - Source object for error location
 * @param nameType - Type of name being validated (e.g., 'Skill name', 'Attribute name')
 * @returns The original string (validation error will be reported via diagnostics)
 */
function validateName(
    str: string,
    diagnostics: Context['diagnostics'],
    source: Shape,
    nameType: string = 'Name'
): string {
    // Match AI Studio's validation: only alphanumeric characters and spaces
    const regex = /^[a-zA-Z0-9 ]+$/
    if (!regex.test(str)) {
        // Find and show specific invalid characters
        const invalidChars = str.match(/[^a-zA-Z0-9 ]/g)
        const invalidCharsStr = invalidChars ? [...new Set(invalidChars)].join(', ') : ''

        diagnostics.error(
            source,
            `${nameType} '${str}' contains invalid character(s): ${invalidCharsStr}. ` +
                `${nameType}s must contain only letters, numbers, and spaces. ` +
                `Please remove special characters, underscores, and punctuation.`
        )
    }
    return str
}

/**
 * Generate internal name from display name
 * Converts display names like 'Travel Destination' to 'travel_destination'
 * Matches AI Studio's convertAttributeLabelToName logic exactly
 * @param displayName - The user-friendly display name
 * @returns Clean internal name suitable for database storage
 */
function generateInternalName(displayName: string): string {
    // Convert to lowercase and replace one or more spaces with underscores
    return displayName.toLowerCase().trim().replace(/\s+/g, '_')
}

// Destructure table names for easier usage
const {
    SN_NOWASSIST_SKILL_CONFIG,
    SN_NOWASSIST_SKILL_CONFIG_STATUS,
    SYS_ONE_EXTEND_CAPABILITY,
    SYS_ONE_EXTEND_CAPABILITY_DEFINITION,
    SYS_GENERATIVE_AI_CONFIG,
    SYS_GENERATIVE_AI_PROMPT_CONFIG,
    SYS_GENERATIVE_AI_VALIDATOR,
    SYS_GENERATIVE_AI_REQUEST_VALIDATOR,
    SYS_GENERATIVE_AI_RESPONSE_VALIDATOR,
    SYS_GEN_AI_FEATURE_MAPPING,
    SYS_GEN_AI_STRATEGY_MAPPING,
    SYS_ONE_EXTEND_DEFINITION_CONFIG,
    SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE,
    SN_NOWASSIST_SKILL_CONFIG_VAR_SET,
    SYS_GEN_AI_SKILL,
    SYS_GEN_AI_SKILL_APPLICABILITY,
    SYS_GEN_AI_SKILL_CONFIG,
    SYS_VARIABLE_VALUE,
} = TABLE_NAMES

/**
 * Process skill configuration
 * Maps UI-aligned structure to ServiceNow records
 */
export async function processSkill(
    callExpression: CallExpressionShape,
    definitionArg: ObjectShape,
    promptConfigArg: ObjectShape,
    context: Context
) {
    const { factory, diagnostics, logger } = context

    // Extract skill structure from definition (arg 1)
    const $id = definitionArg.get('$id')
    const name = definitionArg.get('name')?.asString().getValue()
    const description = definitionArg.has('description')
        ? definitionArg.get('description')?.asString().getValue()
        : undefined
    const state = definitionArg.has('state') ? definitionArg.get('state')?.asString().getValue() : undefined

    // Validate required fields
    if (!name || name.trim() === '') {
        diagnostics.error(definitionArg, 'NowAssistSkillConfig requires a name')
        return { success: false as const }
    } else if (name.length > SKILL_NAME_MAX_LENGTH) {
        diagnostics.error(
            definitionArg,
            `Skill name must not exceed ${SKILL_NAME_MAX_LENGTH} characters (current length: ${name.length})`
        )
        return { success: false as const }
    }

    // Validate skill name: only alphanumeric characters and spaces allowed
    validateName(name, diagnostics, definitionArg, 'Skill name')

    // promptConfigArg is the second function argument (already validated as ObjectShape in the plugin)

    const providersData: {
        providerName: string
        flowId: string
        flowType: string
        providerShape: ObjectShape
        prompts: { promptObj: ObjectShape; versions: ObjectShape[] }[]
        defaultPromptName: string
        defaultPromptVersion?: number
        isDefaultProvider: boolean
    }[] = []
    const allPromptNames = new Set<string>() // Track prompt names across entire skill

    const providers = promptConfigArg.get('providers').asArray().getElements()

    if (providers.length === 0) {
        diagnostics.error(promptConfigArg, 'providers array cannot be empty')
        return { success: false as const }
    }

    // Get defaultProvider - if not specified, use first provider's name
    let defaultProviderName = promptConfigArg.has('defaultProvider')
        ? promptConfigArg.get('defaultProvider')?.asString().getValue()
        : undefined

    // Extract all providers and their prompts
    for (let providerIndex = 0; providerIndex < providers.length; providerIndex++) {
        const providerObj = providers[providerIndex]!.asObject()

        // Extract provider info
        const providerName = providerObj.get('provider')?.asString().getValue() || 'Now LLM Service'

        // Resolve flow implementation from providerAPI or PROVIDER_MAPPINGS
        let flowId: string
        let flowType: string = 'sys_hub_flow'

        if (providerObj.has('providerAPI')) {
            const providerAPIValue = providerObj.get('providerAPI')

            if (providerAPIValue?.isObject()) {
                // Object format: { type: 'sys_hub_flow', id: '...' }
                const apiObj = providerAPIValue.asObject()
                flowType = apiObj.get('type')?.asString().getValue() || 'sys_hub_flow'
                flowId = apiObj.get('id')?.asString().getValue() || NOW_LLM_GENERIC_PROVIDER_API
            } else if (providerAPIValue?.isString()) {
                // String format: direct sys_id (backward compatible)
                flowId = providerAPIValue.asString().getValue() || NOW_LLM_GENERIC_PROVIDER_API
            } else {
                flowId = NOW_LLM_GENERIC_PROVIDER_API
            }
        } else {
            // No providerAPI - look up by provider name in PROVIDER_MAPPINGS
            const mapping = PROVIDER_MAPPINGS[providerName]

            if (mapping) {
                flowId = mapping.flow_id
                flowType = mapping.flow_type
            } else {
                // Unknown provider without explicit providerAPI
                diagnostics.error(
                    providerObj,
                    `Unknown provider "${providerName}". Please either:\n` +
                        `1. Use a known provider: ${Object.keys(PROVIDER_MAPPINGS).join(', ')}\n` +
                        `2. Provide explicit providerAPI: { type: 'sys_hub_flow', id: 'your_flow_sys_id' }`
                )
                return { success: false as const }
            }
        }

        // Set default provider to first one if not specified
        if (!defaultProviderName && providerIndex === 0) {
            defaultProviderName = providerName
        }

        // Extract prompts for this provider
        const prompts = providerObj.get('prompts').asArray().getElements()

        const providerPrompts: { promptObj: ObjectShape; versions: ObjectShape[] }[] = []

        // Get defaultPrompt and defaultPromptVersion for this provider
        let providerDefaultPromptName = providerObj.has('defaultPrompt')
            ? providerObj.get('defaultPrompt')?.asString().getValue()
            : undefined

        const providerDefaultPromptVersion = providerObj.has('defaultPromptVersion')
            ? providerObj.get('defaultPromptVersion')?.asNumber().getValue()
            : undefined

        for (let promptIndex = 0; promptIndex < prompts.length; promptIndex++) {
            const promptObj = prompts[promptIndex]!.asObject()
            const promptItemName = promptObj.get('name')?.asString().getValue() || ''

            // Validate prompt name: only alphanumeric characters and spaces allowed
            validateName(promptItemName, diagnostics, promptObj, 'Prompt name')

            // Check for duplicate names across entire skill
            if (allPromptNames.has(promptItemName)) {
                diagnostics.error(
                    promptObj,
                    `Duplicate prompt name '${promptItemName}'. Each prompt must have a unique name across the entire skill.`
                )
                return { success: false as const }
            }
            allPromptNames.add(promptItemName)

            // Set default prompt to first one if not specified
            if (!providerDefaultPromptName && promptIndex === 0) {
                providerDefaultPromptName = promptItemName
            }

            // Extract versions for this prompt
            const versions = promptObj.get('versions').asArray().getElements()
            if (versions.length === 0) {
                diagnostics.error(
                    promptObj,
                    `Prompt '${promptItemName}' must have at least one version in the versions array.`
                )
                return { success: false as const }
            }

            // Validate version number format: must be a positive integer (e.g., 1, 2, 3)
            const versionNumbers = new Set<number>()
            for (const v of versions) {
                const versionObj = v.asObject()
                const versionNumber = versionObj.has('version')
                    ? versionObj.get('version')?.asNumber().getValue() || 1
                    : 1

                // Check if version is a positive integer
                if (!Number.isInteger(versionNumber) || versionNumber < 1) {
                    diagnostics.error(
                        versionObj,
                        `Version '${versionNumber}' of prompt '${promptItemName}' must be a positive integer (e.g., 1, 2, 3).`
                    )
                    return { success: false as const }
                }

                // Check for duplicate version numbers
                if (versionNumbers.has(versionNumber)) {
                    diagnostics.error(
                        versionObj,
                        `Duplicate version '${versionNumber}' found in prompt '${promptItemName}'. ` +
                            `Each version must have a unique version number within the same prompt.`
                    )
                    return { success: false as const }
                }
                versionNumbers.add(versionNumber)
            }

            // Validate version states when there are multiple versions
            if (versions.length > 1) {
                // Extract version numbers and states
                const versionData = versions.map((v, index) => {
                    const versionObj = v.asObject()
                    const versionNumber = versionObj.has('version')
                        ? versionObj.get('version')?.asNumber().getValue() || 1
                        : 1
                    const promptState = versionObj.has('promptState')
                        ? versionObj.get('promptState')?.asString().getValue() || 'draft'
                        : 'draft'
                    return { versionObj, versionNumber, promptState, index }
                })

                // Sort by version number in descending order to find highest version
                const sortedVersionsDesc = [...versionData].sort((a, b) => {
                    return b.versionNumber - a.versionNumber
                })

                // Sort by version number in ascending order for sequential validation
                const sortedVersions = [...versionData].sort((a, b) => {
                    return a.versionNumber - b.versionNumber
                })

                const highestVersion = sortedVersionsDesc[0]!
                const lowerVersions = sortedVersionsDesc.slice(1)

                // Validate: Only the highest version can be 'draft'
                for (const versionInfo of lowerVersions) {
                    if (versionInfo.promptState === 'draft') {
                        diagnostics.error(
                            versionInfo.versionObj,
                            `Version '${versionInfo.versionNumber}' of prompt '${promptItemName}' cannot be 'draft'. ` +
                                `Only the highest version (${highestVersion.versionNumber}) can be 'draft'. ` +
                                `Lower versions must be 'published' or 'archived'.`
                        )
                        return { success: false as const }
                    }
                }

                // Validate: All lower versions should be 'published' or 'archived'
                for (const versionInfo of lowerVersions) {
                    if (versionInfo.promptState !== 'published' && versionInfo.promptState !== 'archived') {
                        diagnostics.error(
                            versionInfo.versionObj,
                            `Version '${versionInfo.versionNumber}' of prompt '${promptItemName}' must be 'published' or 'archived'. ` +
                                `Lower versions should be 'published' or 'archived', only the highest version (${highestVersion.versionNumber}) can be 'draft' or 'finalized'.`
                        )
                        return { success: false as const }
                    }
                }

                // Validate: If there are archived versions, there must be at least one published version after them
                // Pattern: archived* -> published+ is valid, but published -> archived is not
                // This allows: archived -> archived -> published, archived -> published, etc.

                // Check 1: Published cannot be followed by archived
                for (let i = 0; i < sortedVersions.length - 1; i++) {
                    const currentVersion = sortedVersions[i]!
                    const nextVersion = sortedVersions[i + 1]!

                    if (currentVersion.promptState === 'published' && nextVersion.promptState === 'archived') {
                        diagnostics.error(
                            currentVersion.versionObj,
                            `Version '${currentVersion.versionNumber}' of prompt '${promptItemName}' is 'published', ` +
                                `but the next higher version (${nextVersion.versionNumber}) is 'archived'. ` +
                                `A published version cannot be followed by an archived version. ` +
                                `Valid pattern: archived -> ... -> archived -> published.`
                        )
                        return { success: false as const }
                    }
                }

                // Check 2: If there are any archived versions, ensure there's at least one published version after all archived versions
                const hasArchivedVersions = sortedVersions.some((v) => v.promptState === 'archived')
                if (hasArchivedVersions) {
                    // Find the last archived version
                    let lastArchivedIndex = -1
                    for (let i = sortedVersions.length - 1; i >= 0; i--) {
                        if (sortedVersions[i]!.promptState === 'archived') {
                            lastArchivedIndex = i
                            break
                        }
                    }

                    // Check if there's at least one published version after the last archived version
                    let hasPublishedAfterArchived = false
                    for (let i = lastArchivedIndex + 1; i < sortedVersions.length; i++) {
                        if (sortedVersions[i]!.promptState === 'published') {
                            hasPublishedAfterArchived = true
                            break
                        }
                    }

                    if (!hasPublishedAfterArchived) {
                        const lastArchivedVersion = sortedVersions[lastArchivedIndex]!
                        diagnostics.error(
                            lastArchivedVersion.versionObj,
                            `Prompt '${promptItemName}' has archived versions, ` +
                                `but no published version after the last archived version (${lastArchivedVersion.versionNumber}). ` +
                                `At least one version after all archived versions must be 'published'. ` +
                                `Valid pattern: archived -> ... -> archived -> published.`
                        )
                        return { success: false as const }
                    }
                }
            }

            providerPrompts.push({ promptObj, versions: versions.map((v) => v.asObject()) })
        }

        // Validate defaultPrompt exists in this provider's prompts
        const providerPromptNames = providerPrompts.map((p) => p.promptObj.get('name')?.asString().getValue())
        if (providerDefaultPromptName && !providerPromptNames.includes(providerDefaultPromptName)) {
            diagnostics.error(
                providerObj,
                `defaultPrompt '${providerDefaultPromptName}' does not match any prompt name in provider '${providerName}'. Available prompts: ${providerPromptNames.join(', ')}`
            )
            return { success: false as const }
        }

        // Validate defaultPromptVersion if specified
        if (providerDefaultPromptVersion) {
            // Find the default prompt
            const defaultPromptData = providerPrompts.find(
                (p) => p.promptObj.get('name')?.asString().getValue() === providerDefaultPromptName
            )

            if (!defaultPromptData) {
                diagnostics.error(
                    providerObj,
                    `defaultPromptVersion '${providerDefaultPromptVersion}' is specified, but the default prompt '${providerDefaultPromptName}' was not found. ` +
                        `Available prompts: ${providerPrompts.map((p) => p.promptObj.get('name')?.asString().getValue()).join(', ')}`
                )
                return { success: false as const }
            }

            // Find the version that matches defaultPromptVersion in the default prompt
            const defaultVersionObj = defaultPromptData.versions.find((v) => {
                const versionNumber = v.has('version') ? v.get('version')?.asNumber().getValue() || 1 : 1
                return versionNumber === providerDefaultPromptVersion
            })

            if (!defaultVersionObj) {
                diagnostics.error(
                    providerObj,
                    `defaultPromptVersion '${providerDefaultPromptVersion}' does not match any version in the default prompt '${providerDefaultPromptName}'. ` +
                        `Available versions: ${defaultPromptData.versions.map((v) => (v.has('version') ? v.get('version')?.asNumber().getValue() || 1 : 1)).join(', ')}`
                )
                return { success: false as const }
            }

            // Validate the default version state
            const defaultVersionState = defaultVersionObj.has('promptState')
                ? defaultVersionObj.get('promptState')?.asString().getValue() || 'draft'
                : 'draft'

            // Rule 1: Archived versions cannot be default
            if (defaultVersionState === 'archived') {
                diagnostics.error(
                    defaultVersionObj,
                    `defaultPromptVersion '${providerDefaultPromptVersion}' of prompt '${providerDefaultPromptName}' has state 'archived'. ` +
                        `Archived versions cannot be set as default.`
                )
                return { success: false as const }
            }

            // Rule 2: Draft versions cannot be default if there are multiple versions
            if (defaultVersionState === 'draft' && defaultPromptData.versions.length > 1) {
                diagnostics.error(
                    defaultVersionObj,
                    `defaultPromptVersion '${providerDefaultPromptVersion}' of prompt '${providerDefaultPromptName}' has state 'draft'. ` +
                        `Draft versions cannot be set as default when there are multiple versions. ` +
                        `Please use a 'published' or 'finalized' version as default.`
                )
                return { success: false as const }
            }
        }

        providersData.push({
            providerName: providerName!,
            flowId: flowId!,
            flowType: flowType!,
            providerShape: providerObj,
            prompts: providerPrompts,
            defaultPromptName: providerDefaultPromptName!,
            ...(providerDefaultPromptVersion !== undefined
                ? { defaultPromptVersion: providerDefaultPromptVersion }
                : {}),
            isDefaultProvider: providerName === defaultProviderName,
        })
    }

    // Validate defaultProvider exists
    const providerNames = providersData.map((p) => p.providerName)
    if (defaultProviderName && !providerNames.includes(defaultProviderName)) {
        diagnostics.error(
            promptConfigArg,
            `defaultProvider '${defaultProviderName}' does not match any provider name. Available providers: ${providerNames.join(', ')}`
        )
        return { success: false as const }
    }

    // Extract deploymentSettings (object format)
    const deploymentSettingsArg = definitionArg.has('deploymentSettings')
        ? definitionArg.get('deploymentSettings')
        : undefined

    // Extract securityControls
    const securityControls = definitionArg.has('securityControls') ? definitionArg.get('securityControls') : undefined

    // Parse deployment settings object
    let enableUIAction = false
    let uiActionIdShape: Shape | undefined
    let uiActionAutoGenConfig: ObjectShape | null = null
    let enableNAP = false
    let napRoles: string = '' // empty by default; simple-mode default role applied after parsing
    let napConditionMode: string = 'simple' // default condition mode
    let napConditionScript: string | undefined // optional user-provided condition script, defaults to DEFAULT_CONDITION_SCRIPT
    let enableFlowAction = false
    let skillFamilyShape: Shape | undefined

    if (deploymentSettingsArg?.isObject()) {
        const deployObj = deploymentSettingsArg.asObject()

        // Check skillFamily — string sys_id or DbRecord<'sn_nowassist_skill_family'> reference.
        // If not provided, TABLE_DEFAULTS will supply the "Others" family as default.
        if (deployObj.has('skillFamily')) {
            const familyValue = deployObj.get('skillFamily')
            if (familyValue && !familyValue.isUndefined()) {
                // Validate string values are valid GUIDs; DbRecord references pass through
                if (familyValue.isString()) {
                    const strVal = familyValue.asString().getValue()
                    if (!isGUID(strVal)) {
                        diagnostics.error(
                            familyValue,
                            `'skillFamily' must be a valid sys_id (32-character GUID). Received: '${strVal}'`
                        )
                    } else {
                        skillFamilyShape = familyValue
                    }
                } else {
                    // DbRecord reference
                    skillFamilyShape = familyValue
                }
            }
        }

        // Check UIAction - can be either:
        // 1. ExplicitKey (Now.ID['my_action']) - manual reference
        // 2. Object with 'table' field - auto-generation config
        if (deployObj.has('uiAction')) {
            const uiActionConfig = deployObj.get('uiAction')
            if (uiActionConfig && !uiActionConfig.isUndefined()) {
                // Check if it's an object with 'table' field (auto-generation)
                if (uiActionConfig.isObject() && uiActionConfig.asObject().has('table')) {
                    uiActionAutoGenConfig = uiActionConfig
                    enableUIAction = true
                    // uiActionIdShape will be set after auto-generation
                } else {
                    // ExplicitKey format: Now.ID['my_action'] (manual reference)
                    enableUIAction = true
                    uiActionIdShape = uiActionConfig
                }
            }
        }

        // Check NAP - object with { enabled: true, roles: [...] }
        if (deployObj.has('nowAssistPanel')) {
            const napConfig = deployObj.get('nowAssistPanel')
            if (napConfig?.isObject()) {
                const napObj = napConfig.asObject()

                // Check enabled flag
                if (napObj.has('enabled')) {
                    const enabledValue = napObj.get('enabled')?.asBoolean()?.getValue()
                    enableNAP = enabledValue === true
                }

                // Extract conditionMode first — needed for roles validation
                if (napObj.has('conditionMode')) {
                    const conditionModeValue = napObj.get('conditionMode')?.asString()?.getValue()
                    if (conditionModeValue === 'simple' || conditionModeValue === 'advanced') {
                        napConditionMode = conditionModeValue
                    }
                }

                // Extract roles if NAP is enabled. Roles are optional in both modes:
                // simple mode falls back to the 'now_assist_panel_user' default below,
                // advanced mode leaves roles empty so the condition script governs access.
                if (enableNAP && napObj.has('roles')) {
                    const rolesField = napObj.get('roles')

                    if (rolesField?.isArray()) {
                        const rolesArray = rolesField.asArray().getElements()
                        const roleStrings: string[] = []

                        for (const role of rolesArray) {
                            if (role.isString()) {
                                roleStrings.push(role.asString().getValue())
                            } else if (role.isObject()) {
                                // Handle Role() or DbRecord<'sys_user_role'>
                                const roleObj = role.asObject()
                                // Check for 'name' property (Role object)
                                if (roleObj.has('name')) {
                                    const nameVal = roleObj.get('name')?.asString()?.getValue()
                                    if (nameVal) {
                                        roleStrings.push(nameVal)
                                    }
                                }
                                // Check for 'sys_user_role' property (DbRecord)
                                else if (roleObj.has('sys_user_role')) {
                                    const sysUserRole = roleObj.get('sys_user_role')?.asString()?.getValue()
                                    if (sysUserRole) {
                                        roleStrings.push(sysUserRole)
                                    }
                                }
                            }
                        }

                        // Filter out empty strings
                        const validRoles = roleStrings.filter((r) => r.trim().length > 0)

                        // Validate that restricted roles (e.g., 'maint') are not used
                        for (const roleValue of validRoles) {
                            // Check against restricted role names
                            if (RESTRICTED_ROLE_NAMES.includes(roleValue as (typeof RESTRICTED_ROLE_NAMES)[number])) {
                                diagnostics.error(
                                    rolesField,
                                    `The '${roleValue}' role cannot be used for Now Assist Panel roles. ` +
                                        `This role has special system-level privileges and is restricted.`
                                )
                            }
                            // Check against restricted role sys_ids
                            if (
                                RESTRICTED_ROLE_SYS_IDS.includes(roleValue as (typeof RESTRICTED_ROLE_SYS_IDS)[number])
                            ) {
                                diagnostics.error(
                                    rolesField,
                                    `The 'maint' role (sys_id: ${roleValue}) cannot be used for Now Assist Panel roles. ` +
                                        `This role has special system-level privileges and is restricted.`
                                )
                            }
                        }

                        // Empty roles is allowed in both modes: simple mode falls back to
                        // the 'now_assist_panel_user' default below; advanced mode leaves
                        // roles empty so the condition script alone governs panel access.
                        if (validRoles.length > 0) {
                            napRoles = validRoles.join(',')

                            // Warn about missing 'now_assist_panel_user' only in simple mode; advanced mode relies on the condition script.
                            if (napConditionMode !== 'advanced' && !validRoles.includes('now_assist_panel_user')) {
                                diagnostics.warn(
                                    rolesField,
                                    `'now_assist_panel_user' role is recommended for Now Assist Panel access. ` +
                                        `Without this role, users may not be able to access the skill in the panel.`
                                )
                            }
                        }
                    }
                }

                // Extract conditionScript if provided (optional — defaults to DEFAULT_CONDITION_SCRIPT if omitted)
                if (napObj.has('conditionScript')) {
                    napConditionScript = napObj.get('conditionScript')?.asString()?.getValue()
                }

                // Default to 'now_assist_panel_user' in simple mode when no roles were provided.
                // Advanced mode leaves roles empty — the condition script alone governs access.
                if (enableNAP && napRoles === '' && napConditionMode !== 'advanced') {
                    napRoles = SYS_GEN_AI_SKILL_APPLICABILITY_CREATE_ONLY_DEFAULTS.roles
                }
            }
        }

        // Check FlowAction - simple boolean true
        if (deployObj.has('flowAction')) {
            const flowActionValue = deployObj.get('flowAction')?.asBoolean()?.getValue()
            enableFlowAction = flowActionValue === true
        }
    }

    // 1. Create capability record
    const capabilityRecord = await factory.createRecord({
        source: {
            path: callExpression.getOriginalFilePath(),
            content: '',
        },
        table: SYS_ONE_EXTEND_CAPABILITY,
        explicitId: $id,
        properties: definitionArg.transform(({ $ }) => ({
            ...applyDefaults(SYS_ONE_EXTEND_CAPABILITY, $),
            description: $.from('description').def(''),
            name: $.from('name'),
            short_description: $.from('shortDescription').map((v) => v?.toString() || `${name} Skill`),
        })),
    })

    // 1.5. Pre-create NAP applicability record if NAP is enabled (needed for skill record reference)
    let napApplicabilityRecordId: Shape | null = null
    let genAISkillRecord: BuildRecord | null = null
    let napApplicabilityRecord: BuildRecord | null = null
    if (enableNAP) {
        // Create sys_gen_ai_skill record
        genAISkillRecord = await factory.createRecord({
            source: deploymentSettingsArg!,
            table: SYS_GEN_AI_SKILL,
            properties: definitionArg.transform(({ $ }) => ({
                skill_name: $.from('name'),
                skill_document: $.val(capabilityRecord.getId()),
                skill_table: $.val(TABLE_NAMES.SYS_ONE_EXTEND_CAPABILITY),
            })),
        })

        // Create sys_gen_ai_skill_applicability record (inactive by default)
        // Uses roles from API if provided, otherwise falls back to default
        // Apply defaults (condition_mode, etc.) to prevent corrupted records
        napApplicabilityRecord = await factory.createRecord({
            source: deploymentSettingsArg!,
            table: SYS_GEN_AI_SKILL_APPLICABILITY,
            properties: definitionArg.transform(({ $ }) => ({
                ...applyDefaults(SYS_GEN_AI_SKILL_APPLICABILITY, $),
                skill_id: $.val(genAISkillRecord!.getId()),
                roles: $.val(napRoles),
                condition_mode: $.val(napConditionMode),
                condition_script: $.val(napConditionScript || DEFAULT_CONDITION_SCRIPT).toCdata(),
            })),
        })
        napApplicabilityRecordId = napApplicabilityRecord.getId()
    }

    // 2. Create skill config record
    // Note: internal_name will be set by the custom toFile handler in now-assist-skill-plugin.ts
    // This ensures it uses the actual sys_id from the created record
    let skillRecord = await factory.createRecord({
        source: callExpression,
        table: SN_NOWASSIST_SKILL_CONFIG,
        properties: definitionArg.transform(({ $ }) => ({
            ...applyDefaults(SN_NOWASSIST_SKILL_CONFIG, $),
            name: $.from('name'),
            description: $.from('description').def(''),
            skill_table: $.val(SYS_ONE_EXTEND_CAPABILITY),
            skill_id: $.val(capabilityRecord.getId().getValue()),
            state: $.from('state').map((v) => (v?.ifString()?.getValue() === 'published' ? '1' : '0')),
            nap_skill_applicability: $.val(napApplicabilityRecordId || ''),
            // Override skill_family if user provided one in deploymentSettings;
            // otherwise the TABLE_DEFAULTS value ("Others") is used.
            ...(skillFamilyShape && { skill_family: $.val(skillFamilyShape) }),
        })),
    })
    const skillRecordSysID = skillRecord.getId().getValue()
    const skillName = skillRecord.get('name')?.toString().getValue()

    // Generate the internal_name with the actual sys_id
    const sanitizedName = sanitizeSkillName(skillName)
    const internalName = `${sanitizedName}.${skillRecordSysID}`
    skillRecord = skillRecord.merge({
        internal_name: internalName,
    })
    // 3. Create skill config status record (inactive by default - manual activation required)
    // We'll skip setting active from fluent and let the user manually activate it
    const skillConfigStatusRecord = await factory.createRecord({
        source: {
            path: callExpression.getOriginalFilePath(),
            content: '',
        },
        table: SN_NOWASSIST_SKILL_CONFIG_STATUS,
        properties: definitionArg.transform(({ $ }) => ({
            ...applyDefaults(SN_NOWASSIST_SKILL_CONFIG_STATUS, $),
            skill_config: $.val(skillRecord.getId()),
        })),
    })

    // 4. Process providers and their prompts - create records for each
    const promptRecords: Array<{
        promptItem: ObjectShape
        promptItemName: string
        providerName: string
        capabilityDefinitionRecord: BuildRecord
        aiConfigRecord: BuildRecord
        promptConfigRecord: BuildRecord | null
        definitionConfigRecord: BuildRecord
        isDefaultPrompt: boolean
        isDefaultProvider: boolean
    }> = []

    let promptOrder = 0
    for (const providerData of providersData) {
        const {
            providerName,
            flowId,
            flowType,
            providerShape,
            prompts,
            defaultPromptName,
            defaultPromptVersion,
            isDefaultProvider,
        } = providerData

        // Create capability definition for this provider
        const capabilityDefinitionRecord = await factory.createRecord({
            source: providerShape,
            table: SYS_ONE_EXTEND_CAPABILITY_DEFINITION,
            properties: definitionArg.transform(({ $ }) => ({
                ...applyDefaults(SYS_ONE_EXTEND_CAPABILITY_DEFINITION, $),
                name: $.val(`${name} (${providerName})`),
                capability: $.val(capabilityRecord.getId()),
                api_type: $.val(flowType),
                api: $.val(flowId),
                description: $.val(`Definition for ${name}`),
            })),
        })

        // Create definition config record with default flag for default provider
        const definitionConfigRecord = await factory.createRecord({
            source: providerShape,
            table: SYS_ONE_EXTEND_DEFINITION_CONFIG,
            properties: definitionArg.transform(({ $ }) => ({
                ...applyDefaults(SYS_ONE_EXTEND_DEFINITION_CONFIG, $),
                capability: $.val(capabilityRecord.getId()),
                definition: $.val(capabilityDefinitionRecord.getId()),
                default: $.val(isDefaultProvider),
            })),
        })

        // Process each prompt in this provider
        for (const promptData of prompts) {
            const { promptObj, versions } = promptData
            const promptItemName = promptObj.has('name') ? promptObj.get('name')?.asString().getValue() || '' : ''

            // Use defaultPromptVersion from provider level if this is the default prompt
            const defaultVersionSpec = promptItemName === defaultPromptName ? defaultPromptVersion : undefined

            // Sort versions to ensure we process them in order (lowest to highest)
            const sortedVersions = [...versions].sort((a, b) => {
                const versionA = a.has('version') ? a.get('version')?.asNumber().getValue() || 1 : 1
                const versionB = b.has('version') ? b.get('version')?.asNumber().getValue() || 1 : 1
                return versionA - versionB
            })

            // Determine which versions should be archived
            // Rule: If there are multiple published versions, only the last published version stays published
            // All earlier published versions should be archived
            const versionStates = sortedVersions.map((v) => {
                const state = v.has('promptState') ? v.get('promptState')?.asString().getValue() || 'draft' : 'draft'
                return state
            })

            // Find the index of the first published version (for default version fallback)
            let firstPublishedIndex = -1
            for (let i = 0; i < versionStates.length; i++) {
                if (versionStates[i] === 'published') {
                    firstPublishedIndex = i
                    break
                }
            }

            // Find the index of the last published version (for archiving logic)
            let lastPublishedIndex = -1
            for (let i = versionStates.length - 1; i >= 0; i--) {
                if (versionStates[i] === 'published') {
                    lastPublishedIndex = i
                    break
                }
            }

            // Track the last published version's sys_id for parent relationships
            let lastPublishedVersionRecordId: Shape | null = null

            // Compute the default version index BEFORE the loop
            // Priority: 1) explicit defaultPromptVersion, 2) first published version, 3) first version
            let defaultVersionIndex = 0 // fallback to first version
            if (defaultVersionSpec) {
                // Find version matching the explicit defaultPromptVersion number
                const matchIndex = sortedVersions.findIndex((v) => {
                    const ver = v.has('version') ? v.get('version')?.asNumber().getValue() : 1
                    return ver === defaultVersionSpec
                })
                if (matchIndex !== -1) {
                    defaultVersionIndex = matchIndex
                }
            } else if (firstPublishedIndex !== -1) {
                // Use first published version as default
                defaultVersionIndex = firstPublishedIndex
            }
            // else: defaultVersionIndex stays 0 (first version)

            // Process each version of this prompt
            for (let versionIndex = 0; versionIndex < sortedVersions.length; versionIndex++) {
                const versionObj = sortedVersions[versionIndex]!

                // Extract $id for stable prompt version identity (required by TypeScript type system)
                const versionExplicitId = versionObj.get('$id') as NowIdShape

                // Extract filterCondition from version level (each version can have its own filter)
                const versionFilterConditionShape = versionObj.has('filterCondition')
                    ? versionObj.get('filterCondition')
                    : undefined

                // Process filterCondition for this version and build filter_properties JSON
                let filterPropertiesJson: string | undefined
                if (versionFilterConditionShape && versionFilterConditionShape.isObject()) {
                    // Get the raw value from the shape
                    const filterConditionValue = versionFilterConditionShape.getValue()
                    if (filterConditionValue && typeof filterConditionValue === 'object') {
                        // Build a map of input attributes for validation
                        // Maps both display name and normalized name to internal name
                        const inputAttributeMap = new Map<string, { dataType: string; internalName: string }>()
                        const normalizedToDisplay = new Map<string, string>() // normalized -> display
                        if (definitionArg.has('inputs')) {
                            const inputs = definitionArg.get('inputs')
                            const inputElements: Shape[] =
                                inputs && inputs.isArray() ? inputs.asArray().getElements() : []
                            for (const inputAttr of inputElements) {
                                if (inputAttr.isObject()) {
                                    const displayName = inputAttr.has('name')
                                        ? inputAttr.get('name')?.asString().getValue()
                                        : ''
                                    const dataType = inputAttr.has('dataType')
                                        ? inputAttr.get('dataType')?.asString().getValue()
                                        : 'string'
                                    // Generate internal name: 'incident priority' -> 'incident_priority'
                                    const internalName = displayName.toLowerCase().replace(/\s+/g, '_')
                                    if (displayName) {
                                        inputAttributeMap.set(displayName, {
                                            dataType: dataType || 'string',
                                            internalName,
                                        })
                                        normalizedToDisplay.set(internalName, displayName)
                                    }
                                }
                            }
                        }

                        // Validate and convert filter conditions to platform format
                        // Platform format: { "{{internal_name}}": "value" } or { "{{internal_name.field}}": "value" }
                        const platformFilterProperties: Record<string, string> = {}
                        for (const [key, value] of Object.entries(filterConditionValue)) {
                            const isDotNotation = key.includes('.')
                            const baseName = isDotNotation ? key.split('.')[0] : null
                            const displayName = isDotNotation ? normalizedToDisplay.get(baseName!) : key
                            const inputInfo = displayName ? inputAttributeMap.get(displayName) : undefined

                            // Validate key exists
                            if (isDotNotation && !normalizedToDisplay.has(baseName!)) {
                                diagnostics.error(
                                    versionObj,
                                    `filterCondition attribute '${key}' base name '${baseName}' does not match any input attribute. ` +
                                        `Available normalized input names: [${Array.from(normalizedToDisplay.keys()).join(', ')}]. ` +
                                        `Dot-notation keys must start with a normalized input name (lowercase, underscores for spaces).`
                                )
                                continue
                            }

                            if (!isDotNotation && !inputAttributeMap.has(key)) {
                                diagnostics.error(
                                    versionObj,
                                    `filterCondition attribute '${key}' does not match any input attribute. ` +
                                        `Available input attributes: [${Array.from(inputAttributeMap.keys()).join(', ')}]. ` +
                                        `Please ensure the filterCondition keys match the input attribute names exactly.`
                                )
                                continue
                            }

                            // Validate data type is supported
                            if (
                                inputInfo &&
                                !(SUPPORTED_FILTER_CONDITION_DATATYPES as readonly string[]).includes(
                                    inputInfo.dataType
                                )
                            ) {
                                diagnostics.error(
                                    versionObj,
                                    `filterCondition attribute '${key}' has unsupported data type '${inputInfo.dataType}'. ` +
                                        `Supported data types for filterCondition are: [${SUPPORTED_FILTER_CONDITION_DATATYPES.join(', ')}].`
                                )
                                continue
                            }

                            // Valid key - convert to platform format with {{ }} wrapper
                            const platformKey = isDotNotation ? key : inputInfo!.internalName
                            platformFilterProperties[`{{${platformKey}}}`] = value as string
                        }

                        // Only set filter_properties if we have valid entries
                        if (Object.keys(platformFilterProperties).length > 0) {
                            filterPropertiesJson = JSON.stringify(platformFilterProperties)
                        }
                    }
                }

                const promptVersion = versionObj.has('version') ? versionObj.get('version')?.asNumber().getValue() : 1
                const model = versionObj.has('model')
                    ? versionObj.get('model')?.asString().getValue()
                    : 'llm_generic_small'

                // Validate temperature: must be between 0 and 1, default to 0.2
                let temperature = versionObj.has('temperature')
                    ? (versionObj.get('temperature')?.asNumber()?.getValue() ?? 0.2)
                    : 0.2
                if (temperature < 0 || temperature > 1) {
                    temperature = 0.2
                }

                // Validate maxTokens: must be between 0 and MAX_TOKENS_UPPER_BOUND
                const maxTokens = versionObj.has('maxTokens')
                    ? (versionObj.get('maxTokens')?.asNumber()?.getValue() ?? 500)
                    : 500

                if (maxTokens < 0 || maxTokens > MAX_TOKENS_UPPER_BOUND) {
                    diagnostics.error(
                        versionObj,
                        `max_tokens value '${maxTokens}' is out of range. ` +
                            `max_tokens must be between 0 and ${MAX_TOKENS_UPPER_BOUND}. Please adjust the value.`
                    )
                }
                const prompt = extractPromptValue(versionObj)
                let promptState = versionObj.has('promptState')
                    ? versionObj.get('promptState')?.asString().getValue() || 'draft'
                    : 'draft'

                // Auto-archive logic: If this is a published version but not the last published version, archive it
                if (promptState === 'published' && lastPublishedIndex !== -1 && versionIndex < lastPublishedIndex) {
                    promptState = 'archived'
                }

                // Set show_in_prompt_library: true for published state, false otherwise
                const showInPromptLibrary = promptState === 'published'

                // Create generative AI config record for this prompt version
                const aiConfigRecord = await factory.createRecord({
                    source: versionObj,
                    table: SYS_GENERATIVE_AI_CONFIG,
                    explicitId: versionExplicitId,
                    properties: definitionArg.transform(({ $ }) => ({
                        ...applyDefaults(SYS_GENERATIVE_AI_CONFIG, $),
                        name: $.val(promptItemName),
                        definition: $.val(capabilityDefinitionRecord.getId()),
                        definition_table: $.val(SYS_ONE_EXTEND_CAPABILITY_DEFINITION),
                        capability: $.val(capabilityRecord.getId()),
                        model: $.val(model),
                        prompt: $.val(prompt),
                        max_tokens: $.val(maxTokens.toString()),
                        state: $.val(promptState || 'draft'),
                        active: $.val(promptState === 'published'),
                        version: $.val(String(promptVersion)),
                        show_in_prompt_library: $.val(showInPromptLibrary),
                        description: $.val(description || ''),
                        ...(lastPublishedVersionRecordId ? { parent: $.val(lastPublishedVersionRecordId) } : {}),
                    })),
                })

                // Update lastPublishedVersionRecordId if this version is published
                if (promptState === 'published') {
                    lastPublishedVersionRecordId = aiConfigRecord.getId()
                }

                // Determine if this prompt is the default prompt for the provider
                const isDefaultPrompt = promptItemName === defaultPromptName

                // Determine if this version should be marked as default
                // Only ONE version per prompt should have is_default: true
                const isDefaultVersion = versionIndex === defaultVersionIndex

                // Create a prompt_config record for EACH version (each AI config)
                // is_default is set explicitly based on computed defaultVersionIndex
                const promptConfigRecord = await factory.createRecord({
                    source: versionObj,
                    table: SYS_GENERATIVE_AI_PROMPT_CONFIG,
                    properties: definitionArg.transform(({ $ }) => ({
                        ...applyDefaults(SYS_GENERATIVE_AI_PROMPT_CONFIG, $),
                        ai_config: $.val(aiConfigRecord.getId()),
                        definition: $.val(capabilityDefinitionRecord.getId()),
                        filter_type: $.val(filterPropertiesJson ? 'capability_attribute' : 'default'),
                        is_default: $.val(isDefaultPrompt && isDefaultVersion),
                        // Always include filter_properties to ensure it gets cleared when filterCondition is removed
                        filter_properties: $.val(filterPropertiesJson ?? ''),
                    })),
                })

                promptRecords.push({
                    promptItem: versionObj,
                    promptItemName,
                    providerName,
                    capabilityDefinitionRecord,
                    aiConfigRecord,
                    promptConfigRecord: promptConfigRecord,
                    definitionConfigRecord,
                    isDefaultPrompt: isDefaultPrompt,
                    isDefaultProvider,
                })
            }

            promptOrder++
        }
    }

    // Validate: If skill state is 'published', at least one prompt must also be 'published'
    // Note: Platform allows any prompt to be published, not specifically the default prompt
    if (state === 'published') {
        const publishedPrompts = promptRecords.filter((record) => {
            const promptState = record.promptItem.has('promptState')
                ? record.promptItem.get('promptState')?.asString().getValue()
                : 'draft'
            return promptState === 'published'
        })

        if (publishedPrompts.length === 0) {
            diagnostics.error(
                definitionArg,
                `Cannot publish skill without at least one published prompt. ` +
                    `Please ensure at least one prompt has promptState set to 'published'.`
            )
            return { success: false as const }
        }
    }

    // Validate: If skill state is NOT 'published', no prompt can have promptState as 'published'
    if (state !== 'published') {
        for (const record of promptRecords) {
            const promptState = record.promptItem.has('promptState')
                ? record.promptItem.get('promptState')?.asString().getValue()
                : 'draft'

            if (promptState === 'published') {
                diagnostics.error(
                    definitionArg,
                    `Cannot set prompt '${record.promptItemName}' state to 'published' ` +
                        `because the skill state is not 'published'. ` +
                        `Please set the skill state to 'published' before publishing any prompts.`
                )
                return { success: false as const }
            }
        }
    }

    // Get the first definition for legacy attribute processing
    const capabilityDefinitionRecord = promptRecords[0]!.capabilityDefinitionRecord

    // 8. Process input/output attributes from definition
    const { records: attributeRecords, inputNames } = await processAttributes(
        definitionArg,
        capabilityRecord,
        capabilityDefinitionRecord,
        definitionArg,
        factory,
        diagnostics,
        callExpression,
        $id,
        enableNAP
    )

    // 9. Auto-generate UI Action if config object provided
    const uiActionRecords: BuildRecord[] = []
    if (uiActionAutoGenConfig) {
        try {
            const result = await processUIActionDeployment(
                uiActionAutoGenConfig,
                skillRecord,
                capabilityRecord,
                definitionArg,
                definitionArg,
                factory
            )
            // Set the uiActionIdShape to the generated UI Action's ID
            uiActionIdShape = result.uiActionRecord.getId()
            // Store both UI Action and UX Form Action records to include in final output
            uiActionRecords.push(result.uiActionRecord, result.uxFormActionRecord)
        } catch (error: unknown) {
            diagnostics.error(
                definitionArg,
                `UI Action auto-generation error: ${error instanceof Error ? error.message : String(error)}`
            )
        }
    }

    // 10. Process deployment settings (var sets)
    const varSetRecords: BuildRecord[] = []
    if (enableUIAction || enableNAP || enableFlowAction) {
        const deployResult = await processDeploymentSettings(
            {
                EnableUIAction: enableUIAction,
                EnableNAP: enableNAP,
                EnableFlowAction: enableFlowAction,
                ...(uiActionIdShape !== undefined && { uiActionIdShape }), // Only include if defined
            },
            skillRecord,
            deploymentSettingsArg!, // Use deploymentSettingsArg as source
            definitionArg, // Pass definitionArg for transform method
            factory,
            genAISkillRecord // Pass the pre-created genAISkillRecord
        )
        varSetRecords.push(...deployResult.records)
    }

    // 11. Process provider configurations (validators with preprocessor/postprocessor) if present
    const providerRecords: BuildRecord[] = []

    // Check for skillSettings.providers (preferred API structure)
    const skillSettings = definitionArg.has('skillSettings') ? definitionArg.get('skillSettings') : undefined
    if (skillSettings?.isObject() && skillSettings.has('providers')) {
        const providers = skillSettings.get('providers')
        if (providers?.isArray()) {
            const providerRecordsResult = await processProviderArray(
                providers,
                promptRecords,
                definitionArg,
                factory,
                diagnostics
            )
            providerRecords.push(...providerRecordsResult)
        }
    }
    // Fallback: Check for promptConfigArg.providerConfig (legacy support)
    else if (promptConfigArg.has('providerConfig')) {
        const providerConfig = promptConfigArg.get('providerConfig')
        if (providerConfig?.isObject()) {
            const providerRecordsResult = await processProviderConfig(
                providerConfig,
                promptRecords,
                definitionArg,
                factory,
                diagnostics
            )
            providerRecords.push(...providerRecordsResult)
        }
    }

    // 12. Process security controls (role configuration)
    // Note: ACL records are created automatically by ServiceNow at runtime
    const securityRecords: BuildRecord[] = []
    if (securityControls?.isObject()) {
        try {
            const secRecords = await processSecurityControls(
                securityControls,
                name,
                skillRecord
                    .getId()
                    .getValue(), // Pass skill record sys_id for ACL name and agent reference
                definitionArg,
                factory,
                diagnostics
            )
            securityRecords.push(...secRecords)
        } catch (error: unknown) {
            diagnostics.error(
                definitionArg,
                `Security controls error: ${error instanceof Error ? error.message : String(error)}`
            )
        }
    }

    // 13. Create sys_gen_ai_feature_mapping record
    const featureMappingRecord = await factory.createRecord({
        source: {
            path: callExpression.getOriginalFilePath(),
            content: '',
        },
        table: SYS_GEN_AI_FEATURE_MAPPING,
        properties: definitionArg.transform(({ $ }) => ({
            active: $.val(true),
            licensable: $.val(true),
            document: $.val(capabilityRecord.getId()),
            document_table: $.val('sys_one_extend_capability'),
            feature_name: $.from('name'),
        })),
    })

    // Create sys_gen_ai_strategy_mapping record
    const strategyMappingRecord = await factory.createRecord({
        source: {
            path: callExpression.getOriginalFilePath(),
            content: '',
        },
        table: SYS_GEN_AI_STRATEGY_MAPPING,
        properties: definitionArg.transform(({ $ }) => ({
            active: $.val(true),
            feature: $.val(featureMappingRecord.getId()),
            strategy: $.val('CAPABILITY_EXECUTION'),
        })),
    })

    // Return all created records
    // Flatten all prompt-related records from the promptRecords array
    const allPromptRecords = promptRecords.flatMap((pr) =>
        [pr.capabilityDefinitionRecord, pr.aiConfigRecord, pr.promptConfigRecord, pr.definitionConfigRecord].filter(
            (record): record is BuildRecord => record !== null
        )
    )

    // Include NAP applicability record if it was created
    const napRecords = napApplicabilityRecord ? [napApplicabilityRecord] : []

    // 14. Process tools if defined
    const toolsShape = definitionArg.get('tools')
    const toolRecords = await processTools(
        toolsShape,
        capabilityRecord,
        name,
        definitionArg,
        callExpression,
        factory,
        diagnostics,
        inputNames
    )

    return {
        success: true,
        value: capabilityRecord.with(
            skillRecord,
            skillConfigStatusRecord,
            ...allPromptRecords,
            ...attributeRecords,
            featureMappingRecord,
            strategyMappingRecord,
            ...uiActionRecords,
            ...varSetRecords,
            ...providerRecords,
            ...securityRecords,
            ...napRecords,
            ...toolRecords
        ),
    }
}

/**
 * Helper function to process input/output attributes
 * @param definitionShape - The definition argument shape containing inputs and outputs
 * @returns Object containing attribute records and input names for tool validation
 */
export async function processAttributes(
    definitionShape: ObjectShape,
    capabilityRecord: BuildRecord,
    capabilityDefinitionRecord: BuildRecord,
    arg: ObjectShape,
    factory: Context['factory'],
    diagnostics: Context['diagnostics'],
    callExpression: CallExpressionShape,
    skillExplicitId?: Shape,
    enableNAP: boolean = false
): Promise<{ records: BuildRecord[]; inputNames: Set<string> }> {
    const attributeRecords: BuildRecord[] = []

    // Data types not compatible with Now Assist Panel deployment
    const napIncompatibleDataTypes = ['simple_array', 'json_object', 'json_array']

    // Collect input names for cross-validation with tools
    const inputNames = new Set<string>()

    // Process inputs
    if (definitionShape.has('inputs')) {
        const inputs = definitionShape.get('inputs')
        const inputElements: Shape[] = inputs.isArray() ? inputs.asArray().getElements() : []
        if (inputElements.length > 0 || inputs.isArray()) {
            // Validate: Only one input attribute can have glide_record data type
            const glideRecordInputs: string[] = []
            for (const inputAttr of inputElements) {
                if (inputAttr.isObject()) {
                    const dataType = inputAttr.has('dataType')
                        ? inputAttr.get('dataType')?.asString().getValue()
                        : 'string'
                    if (dataType === 'glide_record') {
                        // Use the display name to generate internal name
                        const displayName = inputAttr.has('name')
                            ? inputAttr.get('name')?.asString().getValue()
                            : 'unknown'
                        glideRecordInputs.push(displayName)
                    }
                }
            }

            if (glideRecordInputs.length > 1) {
                diagnostics.error(
                    inputs,
                    `Only one input attribute can have data_type 'glide_record'. ` +
                        `Found ${glideRecordInputs.length} glide_record inputs: [${glideRecordInputs.join(', ')}]. ` +
                        `The glide_record type is designed to reference a single record. ` +
                        `If you need multiple records, consider using 'json_array' or 'simple_array' data types instead.`
                )
                return { records: [], inputNames }
            }

            // Validate: Input names must be unique (both exact and normalized/lowercase)
            // ServiceNow normalizes input names to lowercase internally, so 'userQuery' and 'userquery' would collide
            const normalizedInputNames = new Map<string, string>() // normalized -> original
            for (const inputAttr of inputElements) {
                if (inputAttr.isObject()) {
                    const displayName = inputAttr.has('name') ? inputAttr.get('name')?.asString().getValue() : 'unknown'
                    const normalizedName = normalizeInputFieldName(displayName ?? 'unknown')

                    // Check for exact duplicate
                    if (inputNames.has(displayName)) {
                        diagnostics.error(
                            inputAttr,
                            `Duplicate input name '${displayName}'. Each input must have a unique name.`
                        )
                        return { records: [], inputNames }
                    }

                    // Check for normalized duplicate (e.g., 'userQuery' vs 'userquery')
                    const existingName = normalizedInputNames.get(normalizedName)
                    if (existingName && existingName !== displayName) {
                        diagnostics.error(
                            inputAttr,
                            `Input name '${displayName}' conflicts with '${existingName}'. ` +
                                `Both normalize to '${normalizedName}' internally. ` +
                                `Input names must be unique when normalized to lowercase.`
                        )
                        return { records: [], inputNames }
                    }

                    inputNames.add(displayName)
                    normalizedInputNames.set(normalizedName, displayName)
                }
            }

            for (const inputAttr of inputElements) {
                if (inputAttr.isObject()) {
                    // Get the display name (API field called 'name')
                    const displayName = inputAttr.has('name') ? inputAttr.get('name')?.asString().getValue() : ''

                    // Extract $id for stable record identity (required field, must be Now.ID['key'])
                    const inputIdShape = inputAttr.has('$id') ? inputAttr.get('$id') : undefined
                    if (!inputIdShape || !(inputIdShape instanceof NowIdShape)) {
                        diagnostics.error(inputAttr, `Input attribute '$id' is required and must use Now.ID['key'].`)
                        continue
                    }
                    const inputExplicitId: NowIdShape = inputIdShape

                    // Validate display name exists and is not empty
                    if (!displayName || displayName.trim() === '') {
                        diagnostics.error(inputAttr, 'Attribute name is required and cannot be empty')
                        continue
                    }

                    // Validate the display name doesn't contain underscores or special characters
                    const validatedDisplayName = validateName(displayName, diagnostics, inputAttr, 'Attribute name')

                    // Generate internal name from display name: 'Travel Destination' -> 'travel_destination'
                    const generatedInternalName = generateInternalName(validatedDisplayName)

                    // Use display name as label, generated name as internal name
                    const label = displayName
                    const description = inputAttr.has('description')
                        ? inputAttr.get('description')?.asString().getValue()
                        : ''

                    // Handle mandatory field - could be boolean or string
                    let mandatory = false
                    if (inputAttr.has('mandatory')) {
                        const mandatoryShape = inputAttr.get('mandatory')
                        const mandatoryRawValue = mandatoryShape?.getValue()
                        mandatory =
                            mandatoryRawValue === true || mandatoryRawValue === 'true' || mandatoryRawValue === '1'
                    }

                    const dataType = inputAttr.has('dataType')
                        ? inputAttr.get('dataType')?.asString().getValue()
                        : 'string'

                    // Validate NAP-incompatible data types
                    if (enableNAP && napIncompatibleDataTypes.includes(dataType || '')) {
                        diagnostics.error(
                            inputAttr.has('dataType') ? inputAttr.get('dataType') : inputAttr,
                            `Input attribute '${displayName}': The dataType '${dataType}' is not compatible with Now Assist Panel deployment. ` +
                                `The following data types are NOT supported with nowAssistPanel: ${napIncompatibleDataTypes.join(', ')}. ` +
                                `Please change the dataType or remove the nowAssistPanel deployment setting.`
                        )
                        return { records: [], inputNames }
                    }

                    // testValues field maps to default_value column in the database
                    const testValues = inputAttr.has('testValues')
                        ? inputAttr.get('testValues')?.asString().getValue()
                        : ''

                    // Validate testValues matches the dataType
                    if (testValues && testValues.trim() !== '') {
                        if (dataType === 'numeric') {
                            const numValue = Number(testValues)
                            if (isNaN(numValue)) {
                                diagnostics.error(
                                    inputAttr.has('testValues') ? inputAttr.get('testValues') : inputAttr,
                                    `Input attribute '${displayName}': testValues '${testValues}' is not valid for dataType 'numeric'. ` +
                                        `Please provide a valid number.`
                                )
                                continue
                            }
                        } else if (dataType === 'boolean') {
                            const lowerVal = testValues.toLowerCase()
                            const validBooleans = ['true', 'false', '1', '0']
                            if (!validBooleans.includes(lowerVal)) {
                                diagnostics.error(
                                    inputAttr.has('testValues') ? inputAttr.get('testValues') : inputAttr,
                                    `Input attribute '${displayName}': testValues '${testValues}' is not valid for dataType 'boolean'. ` +
                                        `Please provide 'true', 'false', '1', or '0'.`
                                )
                                continue
                            }
                        } else if (
                            dataType === 'json_object' ||
                            dataType === 'json_array' ||
                            dataType === 'simple_array'
                        ) {
                            try {
                                const parsed = JSON.parse(testValues)
                                if (
                                    dataType === 'json_object' &&
                                    (typeof parsed !== 'object' || Array.isArray(parsed))
                                ) {
                                    throw new Error('Expected JSON object')
                                }
                                if (
                                    (dataType === 'json_array' || dataType === 'simple_array') &&
                                    !Array.isArray(parsed)
                                ) {
                                    throw new Error('Expected JSON array')
                                }
                                if (
                                    dataType === 'json_array' &&
                                    Array.isArray(parsed) &&
                                    !parsed.every(
                                        (item: unknown) =>
                                            typeof item === 'object' && item !== null && !Array.isArray(item)
                                    )
                                ) {
                                    throw new Error('Expected array of objects')
                                }
                            } catch {
                                const expectedFormat =
                                    dataType === 'json_object'
                                        ? 'JSON object'
                                        : dataType === 'json_array'
                                          ? 'JSON array of objects (e.g., [{"key": "value"}])'
                                          : 'JSON array (e.g., ["a", "b", "c"])'
                                diagnostics.error(
                                    inputAttr.has('testValues') ? inputAttr.get('testValues') : inputAttr,
                                    `Input attribute '${displayName}': testValues is not valid for dataType '${dataType}'. ` +
                                        `Please provide a valid ${expectedFormat}.`
                                )
                                continue
                            }
                        }
                    }

                    // Handle truncate field - could be boolean or string
                    let truncate = false
                    if (inputAttr.has('truncate')) {
                        const truncateShape = inputAttr.get('truncate')
                        const truncateRawValue = truncateShape?.getValue()
                        truncate = truncateRawValue === true || truncateRawValue === 'true' || truncateRawValue === '1'
                    }

                    // Validate truncate is not used with unsupported data types
                    const nonTruncatableDataTypes = ['simple_array', 'json_object', 'json_array']
                    if (truncate && nonTruncatableDataTypes.includes(dataType || '')) {
                        diagnostics.error(
                            inputAttr.has('truncate') ? inputAttr.get('truncate') : inputAttr,
                            `Input attribute '${displayName}': The 'truncate' property is not supported for dataType '${dataType}'. ` +
                                `Only the following data types support truncate: string, numeric, boolean, record, glide_record. ` +
                                `Please remove the 'truncate' property or change the dataType.`
                        )
                        return { records: [], inputNames }
                    }

                    // Handle schema field for glide_record data type
                    let schema = ''
                    if (dataType === 'glide_record') {
                        // Check if user provided tableName and tableSysId separately
                        const tableName = inputAttr.has('tableName')
                            ? inputAttr.get('tableName')?.asString().getValue()
                            : ''

                        // Handle tableSysId which can be either a string or Now.Internal.ExplicitKey object
                        let tableSysId = ''
                        if (inputAttr.has('tableSysId')) {
                            const tableSysIdShape = inputAttr.get('tableSysId')
                            const rawValue = tableSysIdShape?.getValue()

                            // Check if it's an ExplicitKey object (has 'id' property)
                            if (rawValue && typeof rawValue === 'object' && 'id' in rawValue) {
                                tableSysId = typeof rawValue.id === 'string' ? rawValue.id : ''
                            } else if (typeof rawValue === 'string') {
                                tableSysId = rawValue
                            }
                        }

                        // Validate: tableName is required for glide_record, tableSysId is required when testValues is provided
                        if (!tableName || (testValues && !tableSysId)) {
                            diagnostics.error(
                                inputAttr,
                                `dataType 'glide_record' requires 'tableName'${testValues ? " and 'tableSysId' when 'testValues' is provided" : ''}. ` +
                                    `Missing: ${!tableName ? 'tableName' : ''}${!tableName && testValues && !tableSysId ? ' and ' : ''}${testValues && !tableSysId ? 'tableSysId' : ''}.`
                            )
                            continue
                        }

                        if (tableName) {
                            // Construct schema JSON from table_name and table_sys_id (sysId defaults to empty string)
                            schema = JSON.stringify({ tableName, sysId: tableSysId || '' })
                        } else if (inputAttr.has('schema')) {
                            // Fallback to schema field if provided
                            const schemaValue = inputAttr.get('schema')
                            if (schemaValue?.isObject()) {
                                // If schema is an object, stringify it
                                schema = JSON.stringify(schemaValue.getValue())
                            } else if (schemaValue?.isString()) {
                                // If schema is already a string, use it directly
                                schema = schemaValue.asString().getValue()
                            }
                        }
                    }

                    const attrRecord = await factory.createRecord({
                        source: inputAttr,
                        table: SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE,
                        explicitId: inputExplicitId,
                        properties: arg.transform(({ $ }) => {
                            const properties = {
                                ...applyDefaults(SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE, $),
                                name: $.val(generatedInternalName),
                                label: $.val(label),
                                description: $.val(description),
                                capability: $.val(capabilityRecord.getId()),
                                type: $.val('input'),
                                data_type: $.val(dataType),
                                mandatory: $.val(mandatory),
                                truncate: $.val(truncate),
                                default_value: $.val(testValues), // test_values maps to default_value column
                                // NOTE: 'order' and 'hidden' intentionally omitted - let platform handle defaults
                            }

                            // Add schema field if data type is glide_record
                            if (dataType === 'glide_record' && schema) {
                                return { ...properties, schema: $.val(schema) } as typeof properties & {
                                    schema: unknown
                                }
                            }

                            return properties
                        }),
                    })
                    attributeRecords.push(attrRecord)
                }
            }
        }
    }

    // Process outputs (add standard outputs + custom ones)
    const standardOutputs = STANDARD_OUTPUT_NAMES

    // Single pass over outputs array: collect standard output $ids and custom output elements
    const stdOutputIdMap = new Map<string, typeof undefined | ReturnType<typeof definitionShape.get>>()
    const outputsShape = definitionShape.get('outputs')
    const customOutputElements: (typeof outputsShape)[] = []
    if (outputsShape.isArray()) {
        for (const elem of outputsShape.asArray().getElements()) {
            if (elem.isObject()) {
                const nameVal = elem.has('name') ? elem.get('name')?.asString().getValue() : ''
                const internalName = nameVal ? generateInternalName(nameVal) : ''
                if (nameVal && (standardOutputs as readonly string[]).includes(internalName)) {
                    const idShape = elem.has('$id') ? elem.get('$id') : undefined
                    stdOutputIdMap.set(nameVal, idShape)
                } else {
                    customOutputElements.push(elem)
                }
            }
        }
    }
    // For missing standard outputs, derive explicitId from the skill's $id
    const skillIdStr = skillExplicitId ? String(skillExplicitId.getValue()) : undefined
    for (const outputName of standardOutputs) {
        const outputIdShape = stdOutputIdMap.get(outputName)
        // User-provided $id takes priority; otherwise derive from skill $id
        const derivedExplicitId = !outputIdShape && skillIdStr ? `${skillIdStr}__output_${outputName}` : undefined
        const attrRecord = await factory.createRecord({
            source: {
                path: callExpression.getOriginalFilePath(),
                content: '',
            },
            table: SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE,
            ...(outputIdShape
                ? { explicitId: outputIdShape }
                : derivedExplicitId
                  ? { explicitId: derivedExplicitId }
                  : {}),
            properties: arg.transform(({ $ }) => ({
                ...applyDefaults(SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE, $),
                name: $.val(outputName),
                label: $.val(STANDARD_OUTPUT_DEFAULTS[outputName]?.label || outputName),
                description: $.val(STANDARD_OUTPUT_DEFAULTS[outputName]?.description || ''),
                capability: $.val(capabilityRecord.getId()),
                type: $.val('output'),
                // NOTE: 'order', 'hidden', 'contains_large_input' intentionally omitted - let platform handle defaults
            })),
        })
        attributeRecords.push(attrRecord)
    }

    if (customOutputElements.length > 0) {
        // Validate: Only one output attribute can have glide_record data type
        const glideRecordOutputs: string[] = []
        for (const outputAttr of customOutputElements) {
            if (outputAttr.isObject()) {
                const dataType = outputAttr.has('dataType')
                    ? outputAttr.get('dataType')?.asString().getValue()
                    : 'string'
                if (dataType === 'glide_record') {
                    const displayName = outputAttr.has('name')
                        ? outputAttr.get('name')?.asString().getValue()
                        : 'unknown'
                    glideRecordOutputs.push(displayName)
                }
            }
        }

        if (glideRecordOutputs.length > 1) {
            diagnostics.error(
                outputsShape,
                `Only one output attribute can have data_type 'glide_record'. ` +
                    `Found ${glideRecordOutputs.length} glide_record outputs: [${glideRecordOutputs.join(', ')}]. ` +
                    `The glide_record type is designed to reference a single record. ` +
                    `If you need multiple records, consider using 'json_array' or 'simple_array' data types instead.`
            )
            return { records: [], inputNames }
        }

        // Validate: Output names must be unique and not conflict with input names
        const outputNames = new Set<string>()
        for (const outputAttr of customOutputElements) {
            if (outputAttr.isObject()) {
                const displayName = outputAttr.has('name') ? outputAttr.get('name')?.asString().getValue() : 'unknown'

                // Check for duplicate output names
                if (outputNames.has(displayName)) {
                    diagnostics.error(
                        outputAttr,
                        `Duplicate output name '${displayName}'. Each output must have a unique name.`
                    )
                    return { records: [], inputNames }
                }

                // Check for conflict with input names
                if (inputNames.has(displayName)) {
                    diagnostics.error(
                        outputAttr,
                        `Output name '${displayName}' conflicts with an input name. Input and output names must be unique across both.`
                    )
                    return { records: [], inputNames }
                }

                outputNames.add(displayName)
            }
        }

        for (const outputAttr of customOutputElements) {
            if (outputAttr.isObject()) {
                // Get the display name (API field called 'name')
                const displayName = outputAttr.has('name') ? outputAttr.get('name')?.asString().getValue() : ''

                // Extract $id for stable record identity (required field, must be Now.ID['key'])
                const outputIdShape = outputAttr.has('$id') ? outputAttr.get('$id') : undefined
                if (!outputIdShape || !(outputIdShape instanceof NowIdShape)) {
                    diagnostics.error(outputAttr, `Output attribute '$id' is required and must use Now.ID['key'].`)
                    continue
                }
                const outputExplicitId: NowIdShape = outputIdShape

                // Validate display name exists and is not empty
                if (!displayName || displayName.trim() === '') {
                    diagnostics.error(outputAttr, 'Attribute name is required and cannot be empty')
                    continue
                }

                // Validate the display name doesn't contain underscores or special characters
                const validatedDisplayName = validateName(displayName, diagnostics, outputAttr, 'Attribute name')

                // Generate internal name from display name: 'Travel Response' -> 'travel_response'
                const generatedInternalName = generateInternalName(validatedDisplayName)

                const label = displayName
                const description = outputAttr.has('description')
                    ? outputAttr.get('description')?.asString().getValue()
                    : ''
                const dataType = outputAttr.has('dataType')
                    ? outputAttr.get('dataType')?.asString().getValue()
                    : 'string'

                // Validate NAP-incompatible data types
                if (enableNAP && napIncompatibleDataTypes.includes(dataType || '')) {
                    diagnostics.error(
                        outputAttr.has('dataType') ? outputAttr.get('dataType') : outputAttr,
                        `Output attribute '${displayName}': The dataType '${dataType}' is not compatible with Now Assist Panel deployment. ` +
                            `The following data types are NOT supported with nowAssistPanel: ${napIncompatibleDataTypes.join(', ')}. ` +
                            `Please change the dataType or remove the nowAssistPanel deployment setting.`
                    )
                    continue
                }

                // Handle schema field for glide_record data type
                let schema = ''
                if (dataType === 'glide_record') {
                    const tableName = outputAttr.has('tableName')
                        ? outputAttr.get('tableName')?.asString().getValue()
                        : ''

                    // Validate: tableName is required for glide_record outputs
                    if (!tableName) {
                        diagnostics.error(outputAttr, `dataType 'glide_record' requires 'tableName'.`)
                        continue
                    }

                    // Construct schema JSON with tableName and empty sysId
                    schema = JSON.stringify({ tableName, sysId: '' })
                }

                const attrRecord = await factory.createRecord({
                    source: outputAttr,
                    table: SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE,
                    explicitId: outputExplicitId,
                    properties: arg.transform(({ $ }) => {
                        const properties = {
                            ...applyDefaults(SYS_ONE_EXTEND_DEFINITION_ATTRIBUTE, $),
                            name: $.val(generatedInternalName),
                            label: $.val(label),
                            description: $.val(description),
                            capability: $.val(capabilityRecord.getId()),
                            type: $.val('output'),
                            data_type: $.val(dataType),
                            // NOTE: 'order', 'hidden' intentionally omitted - let platform handle defaults
                        }

                        // Add schema field if data type is glide_record
                        if (dataType === 'glide_record' && schema) {
                            return { ...properties, schema: $.val(schema) } as typeof properties & {
                                schema: unknown
                            }
                        }

                        return properties
                    }),
                })
                attributeRecords.push(attrRecord)
            }
        }
    }

    return { records: attributeRecords, inputNames }
}

/**
 * Helper function to process deployment settings (var sets)
 */
export async function processDeploymentSettings(
    deploymentSettings: {
        EnableUIAction?: boolean
        EnableNAP?: boolean
        EnableFlowAction?: boolean
        uiActionIdShape?: Shape
    },
    skillRecord: BuildRecord,
    deploymentSettingsShape: Shape,
    arg: ObjectShape,
    factory: Context['factory'],
    genAISkillRecord: BuildRecord | null = null
): Promise<{ records: BuildRecord[] }> {
    const records: BuildRecord[] = []
    const skillId = skillRecord.getId()
    const skillName = skillRecord.get('name')?.getValue() || 'Skill'

    // Create NAP-related records if EnableNAP is true
    if (deploymentSettings.EnableNAP && genAISkillRecord) {
        // sys_gen_ai_skill and sys_gen_ai_skill_applicability were already created earlier
        // We only need to create sys_gen_ai_skill_config here

        // Add the pre-created genAISkillRecord to records for proper relationship tracking
        records.push(genAISkillRecord)

        // Create sys_gen_ai_skill_config record (inactive by default)
        const genAISkillConfigRecord = await factory.createRecord({
            source: deploymentSettingsShape,
            table: SYS_GEN_AI_SKILL_CONFIG,
            properties: arg.transform(({ $ }) => ({
                skill_id: $.val(genAISkillRecord.getId()),
                hint: $.val(skillName),
            })),
        })
        records.push(genAISkillConfigRecord)
    }

    // Create Flow Action var set if EnableFlowAction is true
    if (deploymentSettings.EnableFlowAction) {
        const flowActionVarSetRecord = await factory.createRecord({
            source: deploymentSettingsShape,
            table: SN_NOWASSIST_SKILL_CONFIG_VAR_SET,
            properties: arg.transform(({ $ }) => ({
                ...applyDefaults(SN_NOWASSIST_SKILL_CONFIG_VAR_SET, $),
                name: $.val('View trigger'),
                skill_config: $.val(skillId),
                config_type: $.val(VAR_SET.CONFIG_TYPE_VIEW_TRIGGER_STEP),
            })),
        })
        records.push(flowActionVarSetRecord)

        // Create sys_variable_value record for Enable Workflow Trigger
        // Value is '0' because FlowAction deployment should not activate the skill
        const enableWorkflowTriggerRecord = await factory.createRecord({
            source: deploymentSettingsShape,
            table: SYS_VARIABLE_VALUE,
            properties: arg.transform(({ $ }) => ({
                document_key: $.val(flowActionVarSetRecord.getId()),
                document: $.val(SN_NOWASSIST_SKILL_CONFIG_VAR_SET),
                variable: $.val(VAR_SET.ENABLE_WORKFLOW_TRIGGER),
            })),
        })
        records.push(enableWorkflowTriggerRecord)
    }

    // Create deployment configuration var set if any standard deployment options are enabled
    const hasStandardDeployOptions = deploymentSettings.EnableUIAction || deploymentSettings.EnableNAP
    if (!hasStandardDeployOptions) {
        return { records }
    }

    // Create a SINGLE var_set record for all deployment settings
    const varSetRecord = await factory.createRecord({
        source: deploymentSettingsShape,
        table: SN_NOWASSIST_SKILL_CONFIG_VAR_SET,
        properties: arg.transform(({ $ }) => ({
            ...applyDefaults(SN_NOWASSIST_SKILL_CONFIG_VAR_SET, $),
            name: $.val('__delivery_step__'),
            skill_config: $.val(skillId),
            config_type: $.val(VAR_SET.SKILL_BUILDER_DELIVERY_STEP),
            screen_table: $.val('sys_ux_macroponent'),
        })),
    })
    records.push(varSetRecord)

    // Create variable values for each enabled deployment option
    if (deploymentSettings.EnableUIAction) {
        const varValueRecord = await factory.createRecord({
            source: deploymentSettingsShape,
            table: SYS_VARIABLE_VALUE,
            properties: arg.transform(({ $ }) => ({
                document: $.val(SN_NOWASSIST_SKILL_CONFIG_VAR_SET),
                document_key: $.val(varSetRecord.getId()),
                value: $.val('1'),
                variable: $.val(VAR_SET.ENABLE_UI_ACTION),
            })),
        })
        records.push(varValueRecord)
    }

    if (deploymentSettings.EnableNAP) {
        const varValueRecord = await factory.createRecord({
            source: deploymentSettingsShape,
            table: SYS_VARIABLE_VALUE,
            properties: arg.transform(({ $ }) => ({
                document: $.val(SN_NOWASSIST_SKILL_CONFIG_VAR_SET),
                document_key: $.val(varSetRecord.getId()),
                value: $.val('1'),
                variable: $.val(VAR_SET.ENABLE_NAP),
            })),
        })
        records.push(varValueRecord)
    }

    // Add UI Action ID if provided
    if (deploymentSettings.uiActionIdShape) {
        const uiActionIdRecord = await factory.createRecord({
            source: deploymentSettingsShape,
            table: SYS_VARIABLE_VALUE,
            properties: arg.transform(({ $ }) => ({
                document: $.val(SN_NOWASSIST_SKILL_CONFIG_VAR_SET),
                document_key: $.val(varSetRecord.getId()),
                // Use $.val() with the Shape to properly resolve Now.ID to sys_id
                value: $.val(deploymentSettings.uiActionIdShape),
                variable: $.val(VAR_SET.UI_ACTION_ID),
            })),
        })
        records.push(uiActionIdRecord)
    }

    return { records }
}

/**
 * Resolve a preprocessor/postprocessor shape to a script string.
 * Handles:
 *   1. Inline string (IIFE) — returned as-is
 *   2. Now.include('./file.server.js') — file content is read and returned
 *   3. Arrow function — body is extracted and wrapped in an IIFE
 *
 * Similar to how InlineScript's `script` field is resolved in tool-processor.ts.
 */
function resolveValidatorScript(shape: Shape | undefined): string | undefined {
    if (!shape || !shape.isDefined() || shape.isUndefined()) {
        return undefined
    }

    // Now.include() — read the included file content
    if (shape.is(NowIncludeShape)) {
        return (shape as NowIncludeShape).getValue() as string
    }

    // Inline string literal — use as-is
    if (shape.isString()) {
        return shape.asString().getValue()
    }

    // Arrow function — extract body and wrap in IIFE
    if (shape instanceof ArrowFunctionShape) {
        const params = shape.getParameters()
        const paramName = params.length > 0 ? params[0]!.getCode() : 'payload'
        const returnVal = shape.getReturnValue()
        const stmts = shape.getStatements()

        let body: string
        if (returnVal) {
            // Expression body: (payload) => expr
            body = `    return ${returnVal.getCode()};`
        } else if (stmts.length > 0) {
            // Block body: (payload) => { stmts }
            body = stmts.map((s) => `    ${s.getCode()}`).join('\n')
        } else {
            body = ''
        }

        return `(function(${paramName}) {\n${body}\n})(${paramName});`
    }

    // Fallback: try getCode()
    return shape.getCode?.() || undefined
}

/**
 * Helper function to process provider array (validators)
 * Processes skillSettings.providers array structure (preferred API)
 */
export async function processProviderArray(
    providersArray: Shape,
    promptRecords: Array<{
        promptItem: ObjectShape
        promptItemName: string
        providerName: string
        capabilityDefinitionRecord: BuildRecord
        aiConfigRecord: BuildRecord
        promptConfigRecord: BuildRecord | null
        definitionConfigRecord: BuildRecord
        isDefaultPrompt: boolean
        isDefaultProvider: boolean
    }>,
    arg: ObjectShape,
    factory: Context['factory'],
    diagnostics: Context['diagnostics']
) {
    const records: BuildRecord[] = []

    if (!providersArray?.isArray()) {
        return records
    }

    const providers = providersArray.asArray().getElements()
    const validatorCache = new Map<string, BuildRecord>()

    for (let i = 0; i < providers.length; i++) {
        const provider = providers[i]

        if (!provider?.isObject()) {
            continue
        }

        const providerName = provider.has('name') ? provider.get('name')?.asString()?.getValue() : undefined
        const providerIdShape = provider.has('$id') ? provider.get('$id') : undefined
        // Get preprocessor/postprocessor as Shape to handle both function references and strings
        const preprocessorShape = provider.has('preprocessor') ? provider.get('preprocessor') : undefined
        const postprocessorShape = provider.has('postprocessor') ? provider.get('postprocessor') : undefined
        // Note: order removed - platform handles defaults
        // const order = provider.has('order') ? provider.get('order')?.asNumber()?.getValue() || 100 : 100

        if (!providerName) {
            diagnostics.warn(provider, `Provider at index ${i} missing 'name' field`)
            continue
        }

        // Check if preprocessor/postprocessor have values (either function or string)
        const hasPreprocessor = preprocessorShape?.isDefined() && !preprocessorShape.isUndefined()
        const hasPostprocessor = postprocessorShape?.isDefined() && !postprocessorShape.isUndefined()

        // Skip if neither preprocessor nor postprocessor is defined
        if (!hasPreprocessor && !hasPostprocessor) {
            continue
        }

        // Find matching capability definitions for this provider
        const matchingDefinitions = promptRecords

        if (matchingDefinitions.length === 0) {
            diagnostics.warn(provider, `No matching definition found for provider: ${providerName}`)
            continue
        }

        // Resolve preprocessor/postprocessor shapes to script strings
        const preprocessorScript = resolveValidatorScript(preprocessorShape)
        const postprocessorScript = resolveValidatorScript(postprocessorShape)

        // Process preprocessor (request validator)
        if (preprocessorScript) {
            const validatorKey = `${providerName}_request`

            let validatorRecord = validatorCache.get(validatorKey)
            if (!validatorRecord) {
                validatorRecord = await factory.createRecord({
                    source: provider,
                    table: SYS_GENERATIVE_AI_VALIDATOR,
                    ...(providerIdShape ? { explicitId: providerIdShape } : {}),
                    properties: arg.transform(({ $ }) => ({
                        name: $.val(`${providerName} - Request Validator`),
                        validator_script: $.val(preprocessorScript).toCdata(),
                    })),
                })
                validatorCache.set(validatorKey, validatorRecord)
                records.push(validatorRecord)
            }

            for (const promptRecord of matchingDefinitions) {
                const definitionId = promptRecord.capabilityDefinitionRecord.getId()

                const requestValidatorRecord = await factory.createRecord({
                    source: provider,
                    table: SYS_GENERATIVE_AI_REQUEST_VALIDATOR,
                    properties: arg.transform(({ $ }) => ({
                        document_id: $.val(definitionId),
                        document_table: $.val('sys_one_extend_capability_definition'),
                        validator: $.val(validatorRecord.getId()),
                    })),
                })
                records.push(requestValidatorRecord)
            }
        }

        // Process postprocessor (response validator)
        if (postprocessorScript) {
            const validatorKey = `${providerName}_response`

            let validatorRecord = validatorCache.get(validatorKey)
            if (!validatorRecord) {
                validatorRecord = await factory.createRecord({
                    source: provider,
                    table: SYS_GENERATIVE_AI_VALIDATOR,
                    ...(providerIdShape ? { explicitId: providerIdShape } : {}),
                    properties: arg.transform(({ $ }) => ({
                        name: $.val(`${providerName} - Response Validator`),
                        active: $.val(true),
                        validator_script: $.val(postprocessorScript).toCdata(),
                    })),
                })
                validatorCache.set(validatorKey, validatorRecord)
                records.push(validatorRecord)
            }

            for (const promptRecord of matchingDefinitions) {
                const definitionId = promptRecord.capabilityDefinitionRecord.getId()

                const requestValidatorRecord = await factory.createRecord({
                    source: provider,
                    table: SYS_GENERATIVE_AI_RESPONSE_VALIDATOR,
                    properties: arg.transform(({ $ }) => ({
                        active: $.val(true),
                        definition: $.val(definitionId),
                        definition_table: $.val('sys_one_extend_capability_definition'),
                        validator: $.val(validatorRecord.getId()),
                    })),
                })
                records.push(requestValidatorRecord)
            }
        }
    }

    return records
}

/**
 * Helper function to process provider configuration (validators)
 * Creates validator and request validator records for preprocessor/postprocessor scripts
 * Legacy support for promptConfiguration.providerConfig object structure
 */
export async function processProviderConfig(
    providerConfig: ObjectShape,
    promptRecords: Array<{
        promptItem: ObjectShape
        promptItemName: string
        providerName: string
        capabilityDefinitionRecord: BuildRecord
        aiConfigRecord: BuildRecord
        promptConfigRecord: BuildRecord | null
        definitionConfigRecord: BuildRecord
        isDefaultPrompt: boolean
        isDefaultProvider: boolean
    }>,
    arg: ObjectShape,
    factory: Context['factory'],
    diagnostics: Context['diagnostics']
) {
    const records: BuildRecord[] = []

    // Get all provider names from the providerConfig object
    const providerNames = providerConfig.properties ? Object.keys(providerConfig.properties()) : []

    // Track created validators to avoid duplicates and reuse them
    const validatorCache = new Map<string, BuildRecord>()

    for (const providerName of providerNames) {
        const provider = providerConfig.get(providerName)
        if (!provider?.isObject()) {
            continue
        }

        // Get preprocessor/postprocessor as Shape to handle both function references and strings
        const preprocessorShape = provider.has('preprocessor') ? provider.get('preprocessor') : undefined
        const postprocessorShape = provider.has('postprocessor') ? provider.get('postprocessor') : undefined
        // Note: order removed - platform handles defaults
        // const order = provider.has('order') ? provider.get('order')?.asNumber()?.getValue() || 100 : 100

        // Check if preprocessor/postprocessor have values (either function or string)
        const hasPreprocessor = preprocessorShape?.isDefined() && !preprocessorShape.isUndefined()
        const hasPostprocessor = postprocessorShape?.isDefined() && !postprocessorShape.isUndefined()

        // Skip if neither preprocessor nor postprocessor is defined
        if (!hasPreprocessor && !hasPostprocessor) {
            continue
        }

        // Find matching capability definitions for this provider
        // Match by provider name in the promptRecords (check aiConfigRecord's provider_api or filter_properties)
        // For now, apply to all definitions (can be refined based on provider matching logic)
        const matchingDefinitions = promptRecords

        if (matchingDefinitions.length === 0) {
            diagnostics.warn(provider, `No matching definition found for provider: ${providerName}`)
            continue
        }

        // Resolve preprocessor/postprocessor shapes to script strings
        const preprocessorScript = resolveValidatorScript(preprocessorShape)
        const postprocessorScript = resolveValidatorScript(postprocessorShape)

        // Process preprocessor (request validator)
        if (preprocessorScript) {
            const validatorKey = `${providerName}_request`

            // Create validator only once
            let validatorRecord = validatorCache.get(validatorKey)
            if (!validatorRecord) {
                validatorRecord = await factory.createRecord({
                    source: provider,
                    table: SYS_GENERATIVE_AI_VALIDATOR,
                    properties: arg.transform(({ $ }) => ({
                        name: $.val(`${providerName} - Request Validator`),
                        validator_script: $.val(preprocessorScript).toCdata(),
                    })),
                })
                validatorCache.set(validatorKey, validatorRecord)
                records.push(validatorRecord)
            }

            // Create request validator links for each matching definition
            for (const promptRecord of matchingDefinitions) {
                const definitionId = promptRecord.capabilityDefinitionRecord.getId()

                const requestValidatorRecord = await factory.createRecord({
                    source: provider,
                    table: SYS_GENERATIVE_AI_REQUEST_VALIDATOR,
                    properties: arg.transform(({ $ }) => ({
                        document_id: $.val(definitionId),
                        document_table: $.val('sys_one_extend_capability_definition'),
                        validator: $.val(validatorRecord.getId()),
                    })),
                })
                records.push(requestValidatorRecord)
            }
        }

        // Process postprocessor (response validator)
        if (postprocessorScript) {
            const validatorKey = `${providerName}_response`

            // Create validator only once
            let validatorRecord = validatorCache.get(validatorKey)
            if (!validatorRecord) {
                validatorRecord = await factory.createRecord({
                    source: provider,
                    table: SYS_GENERATIVE_AI_VALIDATOR,
                    properties: arg.transform(({ $ }) => ({
                        name: $.val(`${providerName} - Response Validator`),
                        validator_script: $.val(postprocessorScript).toCdata(),
                    })),
                })
                validatorCache.set(validatorKey, validatorRecord)
                records.push(validatorRecord)
            }

            // Create request validator links for each matching definition
            for (const promptRecord of matchingDefinitions) {
                const definitionId = promptRecord.capabilityDefinitionRecord.getId()

                const requestValidatorRecord = await factory.createRecord({
                    source: provider,
                    table: SYS_GENERATIVE_AI_RESPONSE_VALIDATOR,
                    properties: arg.transform(({ $ }) => ({
                        definition: $.val(definitionId),
                        definition_table: $.val('sys_one_extend_capability_definition'),
                        validator: $.val(validatorRecord.getId()),
                    })),
                })
                records.push(requestValidatorRecord)
            }
        }
    }

    return records
}
